/** Numeric wire values — mirrors EPN.TimeAndLeave.Api's own enums exactly (no JsonStringEnumConverter
 * anywhere in EPN). See docs/time-and-attendance.md / docs/time-off.md. */
export const AttendanceEntryStatus = { Submitted: 1, Approved: 2, Rejected: 3 } as const;
export const attendanceEntryStatusLabels: Record<number, string> = { 1: 'Submitted', 2: 'Approved', 3: 'Rejected' };

export const LeaveRequestStatus = { Submitted: 1, Approved: 2, Rejected: 3, Cancelled: 4 } as const;
export const leaveRequestStatusLabels: Record<number, string> = { 1: 'Submitted', 2: 'Approved', 3: 'Rejected', 4: 'Cancelled' };

export const LeavePlanType = { Vacation: 1, Sick: 2, Personal: 3, Bereavement: 4, Other: 5 } as const;
export const leavePlanTypeLabels: Record<number, string> = { 1: 'Vacation', 2: 'Sick', 3: 'Personal', 4: 'Bereavement', 5: 'Other' };

export const LeaveAccrualType = { Tenured: 1, FlatAnnual: 2 } as const;
export const leaveAccrualTypeLabels: Record<number, string> = { 1: 'Based on length of service', 2: 'Flat yearly amount' };

export const LeaveResetPeriod = { Annually: 1, Anniversary: 2 } as const;
export const leaveResetPeriodLabels: Record<number, string> = { 1: 'Every calendar year', 2: 'On the employee\'s hire anniversary' };

/** What the server needs to create a leave plan (EPN.TimeAndLeave.Api CreateLeavePlanRequest). Accrual tiers are not part of the inline form. */
export interface CreateLeavePlanInput {
  name: string;
  planType: number;
  annualCapHours: number;
  accrualType: number;
  isCarryOverEnabled: boolean;
  maxCarryOverHours: number | null;
  leaveResetPeriod: number;
}

export interface AttendanceEntryResponse {
  id: string;
  employeeId: string;
  workDate: string;
  workedHours: number;
  overtimeHours: number;
  notes: string | null;
  status: number;
  submittedByUserId: string;
  approvedByUserId: string | null;
  approvedAtUtc: string | null;
  rejectionReason: string | null;
  createdAtUtc: string;
  updatedAtUtc: string | null;
}

export interface HoursForPeriodResponse {
  employeeId: string;
  start: string;
  end: string;
  totalWorkedHours: number;
  totalOvertimeHours: number;
}

export interface LeaveRequestDayResponse {
  id: string;
  leaveDate: string;
  hours: number;
}

export interface LeaveRequestResponse {
  id: string;
  employeeId: string;
  leavePlanId: string;
  reason: string | null;
  status: number;
  submittedByUserId: string;
  approvedByUserId: string | null;
  approvedAtUtc: string | null;
  rejectionReason: string | null;
  days: LeaveRequestDayResponse[];
  createdAtUtc: string;
  updatedAtUtc: string | null;
}

export interface LeavePlanResponse {
  id: string;
  name: string;
  planType: number;
  annualCapHours: number;
  accrualType: number;
  isCarryOverEnabled: boolean;
  maxCarryOverHours: number | null;
  leaveResetPeriod: number;
  isActive: boolean;
}

export interface LeavePlanEnrollmentResponse {
  id: string;
  employeeId: string;
  leavePlanId: string;
  enrolledAtUtc: string;
  isActive: boolean;
}

export interface LeaveBalanceResponse {
  id: string;
  employeeId: string;
  leavePlanId: string;
  sourceType: number;
  sourceId: string | null;
  periodStartDate: string | null;
  periodEndDate: string | null;
  leavesTaken: number;
  accruedLeaves: number;
  availableLeaves: number;
  createdAtUtc: string;
}

export interface HolidayResponse {
  id: string;
  companyId: string | null;
  isGlobal: boolean;
  name: string;
  holidayDate: string;
  isFixed: boolean;
  defaultWorkHourCredit: number | null;
  defaultLeaveHourCredit: number | null;
}

export interface EmployeeHolidayAssignmentResponse {
  id: string;
  employeeId: string;
  holidayDefinitionId: string;
  createdAtUtc: string;
}

// ---- Employee leave plan setup (EPN-216 Step 4; docs/leave-plan-setup-contract.md) ----

export const AccrualMethod = { Manual: 1, EveryPayPeriod: 2, HoursWorked: 3, Yearly: 4 } as const;
export const accrualMethodLabels: Record<number, string> = { 1: 'Manually Tracking', 2: 'Every Pay Period', 3: 'Based on Hours Worked', 4: 'Yearly' };
export const ResetMode = { EmployeeHireDate: 1, CalendarYear: 2, CustomDate: 3 } as const;
export const CarryoverMode = { Expires: 1, CarryAll: 2, CarryUpTo: 3 } as const;
export const TenureUnit = { Months: 1, Years: 2 } as const;

export interface LeaveAccrualRule {
  fromTenureValue: number;
  fromTenureUnit: number;
  toTenureValue: number | null;
  toTenureUnit: number | null;
  /** Hours per pay period (Every Pay Period), hours earned (Based on Hours Worked) or hours per year (Yearly). */
  rate: number;
  perHoursWorked: number | null;
  maximumBalanceHours: number;
}

/** Everything the server accepts when an employee's leave plan is edited. */
export interface LeavePlanSetupSettings {
  accrualMethod: number;
  hoursPerPayPeriod: number | null;
  hoursEarned: number | null;
  perHoursWorked: number | null;
  hoursPerYear: number | null;
  rules: LeaveAccrualRule[];
  resetMode: number;
  resetMonth: number | null;
  resetDay: number | null;
  maximumBalanceHours: number | null;
  carryoverMode: number;
  carryoverLimitHours: number | null;
  negativeBalanceAllowed: boolean;
  maximumNegativeHours: number | null;
}

/** POST /api/employees/{id}/leave-plan-setups. Type, title, effective date and starting balance are fixed at creation. */
export interface LeavePlanSetupRequest extends LeavePlanSetupSettings {
  leavePlanType: number;
  title: string | null;
  effectiveDate: string;
  startingBalanceHours: number;
}

export interface LeavePlanSetupResponse extends LeavePlanSetupSettings {
  id: string;
  employeeId: string;
  leavePlanId: string;
  leavePlanType: number;
  title: string | null;
  displayName: string;
  effectiveDate: string;
  startingBalanceHours: number;
  leaveHours: number;
  accruesAutomatically: boolean;
  isActive: boolean;
  createdAtUtc: string;
  updatedAtUtc: string | null;
  endedAtUtc: string | null;
}

export interface LeavePlanSetupTypeResponse {
  leavePlanId: string;
  title: string;
}

// ---- escoHR mobile Time & Attendance (docs/escohr-time-attendance-contract.md) ----

/** "Unknown" means the entitlement source could not be reached: the page shows "unavailable", never locked or connected. */
export type EscoHrEntitlementState = 'Entitled' | 'NotEntitled' | 'Unknown';

/** The stored state of a connection. What an admin is shown is `EscoHrDisplayStatus`, decided on the server. */
export type EscoHrConnectionStatus = 'Pending' | 'Active' | 'Revoked';

/** The distinct statuses (decision 2026-11-01). Only 'Operational' means attendance-to-payroll processing is enabled. */
export type EscoHrDisplayStatus =
  | 'NotConfigured'
  | 'ConfigurationRequired'
  | 'ConfiguredTestingOnly'
  | 'ConnectedNotOperational'
  | 'Operational'
  | 'SyncError'
  | 'Suspended';

export interface EscoHrConnectionSummary {
  id: string;
  status: EscoHrConnectionStatus;
  /** Stable, opaque, company-scoped and not a secret. Null unless the caller holds integrations.read. Unchanged by a secret rotation. */
  connectionReference: string | null;
  companyId: string;
  createdAtUtc: string;
  revokedAtUtc: string | null;
  activatedAtUtc: string | null;
  lastSuccessfulSyncAtUtc: string | null;
  lastAttemptedSyncAtUtc: string | null;
  signingSecretVersion: number;
  secretLastRotatedAtUtc: string | null;
  configurationVersion: number;
}

export interface EscoHrStatusResponse {
  entitlement: EscoHrEntitlementState;
  entitlementReason: string | null;
  displayStatus: EscoHrDisplayStatus;
  /** True only when displayStatus is 'Operational'. */
  payrollProcessingEnabled: boolean;
  environment: string;
  connection: EscoHrConnectionSummary | null;
  confirmedMappings: number;
  mappingRequiredEvents: number;
  /** Received, mapped events not yet turned into punch records (they are processed only when an admin asks). */
  pendingProcessingEvents: number;
  /** True only where the deployment declared itself a synthetic-data environment (never Production). */
  syntheticDataAvailable: boolean;
}

/** Returned once to the admin who creates the link. Identifies the link; it is not a credential. */
export interface EscoHrConnectionCreatedResponse {
  id: string;
  connectionRef: string;
  status: string;
  createdAtUtc: string;
}

/** The raw secret is in this response only. It is never shown again and must not be kept in state after the dialog closes. */
export interface EscoHrSigningSecretResponse {
  secret: string;
  version: number;
  issuedAtUtc: string;
}

export type EscoHrEventStatus = 'Received' | 'MappingRequired' | 'Rejected';

/** One row of the sync history. Never carries the payload. */
export interface EscoHrEventResponse {
  id: string;
  eventId: string;
  eventType: string;
  status: EscoHrEventStatus;
  statusReason: string | null;
  occurredAtUtc: string | null;
  receivedAtUtc: string;
  escoHrEmployeeRef: string | null;
  mapped: boolean;
  duplicateCount: number;
}

export interface EscoHrMappingResponse {
  id: string;
  escoHrEmployeeRef: string;
  epnEmployeeId: string;
  matchBasis: string;
  confirmedAtUtc: string;
}

export interface CreateEscoHrMappingInput {
  escoHrEmployeeRef: string;
  epnEmployeeId: string;
}

/** A punch exactly as escoHR reported it. Device time and authoritative time are separate and neither is altered. Testing-only: never payroll input. */
export interface EscoHrPunchResponse {
  id: string;
  punchId: string;
  recordVersion: number;
  punchType: 'ClockIn' | 'ClockOut' | 'MealStart' | 'MealEnd' | 'RestStart' | 'RestEnd' | 'Transfer';
  epnEmployeeId: string;
  escoHrEmployeeRef: string;
  authoritativeTimestampUtc: string;
  deviceTimestampUtc: string | null;
  deviceTimeZone: string | null;
  deviceUtcOffsetMinutes: number | null;
  escoHrServerReceivedAtUtc: string | null;
  receivedAtUtc: string;
  offline: boolean;
  method: string | null;
  source: string | null;
  locationRef: string | null;
  departmentRef: string | null;
  jobOrWorkTypeRef: string | null;
  isVoided: boolean;
  isSynthetic: boolean;
}

export type EscoHrLiveState = 'ClockedIn' | 'OnMeal' | 'OnBreak' | 'ClockedOut';

export interface EscoHrLiveAttendanceResponse {
  epnEmployeeId: string;
  state: EscoHrLiveState;
  sinceUtc: string;
  lastPunchAtUtc: string;
  isSynthetic: boolean;
}

export interface EscoHrExceptionResponse {
  source: 'Punch' | 'Event' | 'Mapping';
  code: string;
  severity: 'Error' | 'Warning' | 'Info';
  atUtc: string;
  epnEmployeeId: string | null;
  escoHrEmployeeRef: string | null;
  eventType: string | null;
  punchId: string | null;
  isSynthetic: boolean;
}

export interface EscoHrProcessResult {
  processed: number;
  duplicatesIgnored: number;
  exceptions: number;
  waiting: number;
  notSupportedYet: number;
  remaining: number;
}

export interface EscoHrSyntheticResult {
  batchId: string;
  sent: number;
  accepted: number;
  duplicatesIgnored: number;
  rejected: number;
  mappingRequired: number;
}

// ---- escoHR Time Off, Holidays and Expenses: read-only mirrors (docs/escohr-workforce-contract.md) ----

export type EscoHrWorkforceDomain = 'TimeOff' | 'Holidays' | 'Expenses';

export interface EscoHrDomainSummary {
  domain: EscoHrWorkforceDomain;
  records: number;
  awaitingApproval: number;
  approved: number;
  /** Events whose employee is not mapped, plus mirrored records with a warning. */
  needsAttention: number;
  /** Events that were rejected or could not be recorded. */
  syncErrors: number;
  /** Always zero in this version: nothing mirrored here feeds payroll. */
  readyForPayroll: number;
  /** When the latest event of this domain arrived. EPN is updated only when escoHR sends events. */
  dataAsOfUtc: string | null;
  stale: boolean;
  staleReason: 'SyncError' | 'Suspended' | 'Disconnected' | null;
}

/** The one shared connection status plus the figures for the page asking. */
export interface EscoHrWorkforceSummaryResponse {
  status: EscoHrStatusResponse;
  domain: EscoHrDomainSummary;
}

export interface EscoHrLeaveLineResponse {
  date: string;
  hours: number;
  approvedHours: number | null;
}

export interface EscoHrLeaveRequestResponse {
  id: string;
  recordId: string;
  recordVersion: number;
  epnEmployeeId: string;
  escoHrEmployeeRef: string;
  status: string;
  leaveTypeRef: string | null;
  leavePlanRef: string | null;
  firstDate: string | null;
  lastDate: string | null;
  requestedHours: number;
  approvedHours: number | null;
  partial: boolean;
  emergency: boolean;
  requiresHrReview: boolean;
  approverRef: string | null;
  approvedAtUtc: string | null;
  reasonCode: string | null;
  lines: EscoHrLeaveLineResponse[];
  updatedAtUtc: string;
  isSynthetic: boolean;
}

export interface EscoHrHolidayCalendarResponse {
  id: string;
  holidayRef: string;
  name: string;
  observedDate: string;
  year: number;
  locationRefs: string[];
  updatedAtUtc: string;
  isSynthetic: boolean;
}

export interface EscoHrHolidayRequestResponse {
  id: string;
  recordId: string;
  recordVersion: number;
  epnEmployeeId: string;
  escoHrEmployeeRef: string;
  status: string;
  requestedDate: string;
  holidayRef: string | null;
  observance: string | null;
  hours: number | null;
  updatedAtUtc: string;
  isSynthetic: boolean;
}

export type EscoHrExpenseWarning = 'MissingReceipt' | 'DuplicateReceipt' | 'UnsupportedCurrency' | 'ReductionWithoutReason';

export interface EscoHrExpenseClaimResponse {
  id: string;
  recordId: string;
  recordVersion: number;
  epnEmployeeId: string;
  escoHrEmployeeRef: string;
  status: string;
  expenseDate: string;
  categoryRef: string;
  submittedAmount: number;
  approvedAmount: number | null;
  currency: string;
  reductionReasonCode: string | null;
  departmentRef: string | null;
  locationRef: string | null;
  projectRef: string | null;
  /** Receipt identifiers only. The receipts themselves are never copied to EPN. */
  receiptRefs: string[];
  miles: number | null;
  warnings: EscoHrExpenseWarning[];
  updatedAtUtc: string;
  isSynthetic: boolean;
}
